import { useEffect, useState } from 'react'
import { Box, HStack, Menu, MenuButton, MenuItem, MenuList, Text, VStack } from '@chakra-ui/react'
import { AddIcon, CheckIcon, ChevronDownIcon, ChevronRightIcon, SettingsIcon } from '@chakra-ui/icons'
import { useNavigate } from 'react-router-dom'
import EditAccountModal from 'components/EditAccountModal'
import AddProfileModal from 'components/add-profile-modal'
import { ROLE_BADGE_LABEL } from 'constants/play-profile'
import {
  GOOGLE_LINK_OPEN_ACCOUNT_SESSION_KEY,
  OPEN_MY_ACCOUNT_PASSWORD_SETUP_SESSION_KEY,
  OPEN_MY_ACCOUNT_URL_QUERY
} from 'constants/google-sso'
import { useStores } from 'hooks/useStores'
import { DARK_SURFACE_GRADIENT } from 'constants/dark-tokens'
import routes from 'routes'
import { AddableProfileType } from 'utils/profile-eligibility'
import { PlayProfileSummary } from '../../mock-data'

interface Props {
  profiles: PlayProfileSummary[]
  active: PlayProfileSummary | undefined
  onChange: (p: PlayProfileSummary) => void
  // Force the full trigger (avatar + name + role + chevron + pill bg) regardless
  // of viewport. Default false preserves the responsive base/lg behavior used by
  // MobileShell's inline header. Set true when host is a wide top bar.
  expanded?: boolean
  addableTypes?: AddableProfileType[]
  onAddProfileSuccess?: (kind: AddableProfileType) => void
  onAddProfileConflict?: (kind: AddableProfileType) => void
}

const roleLabel: Record<string, string> = {
  player: 'Player Profile',
  coach: 'Coach Profile',
  child: 'Youth Profile'
}

const Avatar = ({ p, size = 36 }: { p: PlayProfileSummary; size?: number }) => (
  <Box
    width={`${size}px`}
    height={`${size}px`}
    borderRadius="full"
    bg={p.avatarColor}
    overflow="hidden"
    display="flex"
    alignItems="center"
    justifyContent="center"
    color="white"
    fontWeight={400}
    fontSize={`${Math.round(size * 0.4)}px`}
    flexShrink={0}
  >
    {p.avatarUrl ? (
      // Uploaded photo is 3:4 — crop to the circle (cover + overflow hidden).
      <Box as="img" src={p.avatarUrl} alt={p.name} width="100%" height="100%" objectFit="cover" />
    ) : (
      p.initials || '?'
    )}
  </Box>
)

const ProfileSwitcher = ({
  profiles,
  active,
  onChange,
  expanded = false,
  addableTypes,
  onAddProfileSuccess,
  onAddProfileConflict
}: Props) => {
  const { authStore, profileStore } = useStores()
  const { userDetail } = authStore
  const navigate = useNavigate()
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const canAdd = !!(addableTypes && addableTypes.length > 0 && onAddProfileSuccess)

  // Auto-open My Account on Google-link / password-setup post-flow — mirrors the
  // legacy ProfileDropdown behavior so SSO bind and openMyAccount=1 deep links
  // still surface the modal under the new chrome.
  useEffect(() => {
    const id = window.setTimeout(() => {
      const fromGoogleLink = sessionStorage.getItem(GOOGLE_LINK_OPEN_ACCOUNT_SESSION_KEY) === '1'
      const fromPasswordSetup = sessionStorage.getItem(OPEN_MY_ACCOUNT_PASSWORD_SETUP_SESSION_KEY) === '1'
      const urlParams = new URLSearchParams(window.location.search)
      const fromPasswordQuery = urlParams.get(OPEN_MY_ACCOUNT_URL_QUERY) === '1'
      if (!fromGoogleLink && !fromPasswordSetup && !fromPasswordQuery) return
      if (fromPasswordQuery) {
        urlParams.delete(OPEN_MY_ACCOUNT_URL_QUERY)
        const nextPath = `${window.location.pathname}${urlParams.toString() ? `?${urlParams.toString()}` : ''}${window.location.hash}`
        window.history.replaceState({}, '', nextPath)
      }
      setIsAccountModalOpen(true)
    }, 0)
    return () => window.clearTimeout(id)
  }, [])

  const toggleAccountModal = () => {
    if (isAccountModalOpen) {
      sessionStorage.removeItem(OPEN_MY_ACCOUNT_PASSWORD_SETUP_SESSION_KEY)
      sessionStorage.removeItem(GOOGLE_LINK_OPEN_ACCOUNT_SESSION_KEY)
    }
    setIsAccountModalOpen(prev => !prev)
  }

  const safeProfiles = Array.isArray(profiles) ? profiles : []
  const safeActive = active ?? safeProfiles[0]
  if (!safeActive) return null
  const switchProfiles = safeProfiles.filter(p => p.id !== safeActive.id)

  // When expanded, lock the trigger to the full pill layout at every viewport.
  // Otherwise fall through to the responsive base/lg behavior used inside the
  // mobile inline header.
  const triggerBg = expanded ? 'transparent' : { base: 'transparent', lg: 'rgba(255,255,255,0.06)' }
  const triggerPx = expanded ? 3 : { base: 0, lg: 3 }
  const triggerPy = expanded ? 2 : { base: 0, lg: 2 }
  const triggerHoverBg = expanded
    ? 'rgba(255,255,255,0.08)'
    : { base: 'transparent', lg: 'rgba(255,255,255,0.1)' }
  const labelDisplay = expanded ? 'flex' : { base: 'none', lg: 'flex' }
  const chevronDisplay = expanded ? 'inline-block' : { base: 'none', lg: 'inline-block' }

  return (
    <Menu>
      <MenuButton
        as={Box}
        cursor="pointer"
        bg={triggerBg}
        borderRadius="999px"
        px={triggerPx}
        py={triggerPy}
        border="none"
        outline="none"
        _hover={{ bg: triggerHoverBg }}
        _focus={{ boxShadow: 'none', outline: 'none' }}
        _focusVisible={{ boxShadow: 'none', outline: 'none' }}
        sx={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <HStack spacing={2}>
          <Avatar p={safeActive} size={36} />
          <VStack spacing={0} align="flex-start" display={labelDisplay}>
            <Text color="white" fontSize="xs" fontWeight={400} m={0} lineHeight={1.1}>
              {safeActive.name}
            </Text>
            <Text color="rgba(255,255,255,0.6)" fontSize="10px" m={0} lineHeight={1.1} textTransform="uppercase">
              {ROLE_BADGE_LABEL[safeActive.role] ?? safeActive.role}
            </Text>
          </VStack>
          <ChevronDownIcon color="white" display={chevronDisplay} />
        </HStack>
      </MenuButton>
      <MenuList
        bg={DARK_SURFACE_GRADIENT}
        borderColor="rgba(107,121,143,0.22)"
        borderRadius="18px"
        boxShadow="0 18px 40px rgba(0,0,0,0.32)"
        w="250px"
        minW="250px"
        maxW="250px"
        overflow="hidden"
        p={0}
      >
        <Box bg="rgba(255,255,255,0.02)">
          <HStack spacing={3} px={4} py={3.5}>
            <Avatar p={safeActive} size={48} />
            <VStack spacing={0.5} align="flex-start" flex={1}>
              <Text color="#F4F8FB" fontSize="16px" fontWeight={400} m={0}>
                {safeActive.name}
              </Text>
              <Text color="rgba(244,248,251,0.62)" fontSize="13px" m={0}>
                {roleLabel[safeActive.role] ?? safeActive.role}
              </Text>
            </VStack>
            <CheckIcon color="#2bd48f" boxSize={4} />
          </HStack>
        </Box>

        {switchProfiles.length > 0 && (
          <Box py={2}>
            <Text color="rgba(244,248,251,0.48)" fontSize="11px" letterSpacing="0.16em" m={0} px={4} py={2}>
              SWITCH PROFILE
            </Text>

            {switchProfiles.map(p => (
              <MenuItem
                key={p.id}
                bg="transparent"
                border="none"
                _hover={{ bg: 'rgba(255,255,255,0.05)' }}
                onClick={() => onChange(p)}
                px={4}
                py={3}
              >
                <HStack spacing={3} width="100%">
                  <Avatar p={p} size={44} />
                  <VStack spacing={0.5} align="flex-start" flex={1}>
                    <Text color="#F4F8FB" fontSize="16px" fontWeight={400} m={0}>
                      {p.name}
                    </Text>
                    <Text color="rgba(244,248,251,0.58)" fontSize="13px" m={0}>
                      {roleLabel[p.role] ?? p.role}
                    </Text>
                  </VStack>
                  <ChevronRightIcon color="rgba(244,248,251,0.38)" boxSize={5} />
                </HStack>
              </MenuItem>
            ))}
          </Box>
        )}

        {canAdd && (
          <Box py={1}>
            <MenuItem
              bg="transparent"
              border="none"
              _hover={{ bg: 'rgba(255,255,255,0.05)' }}
              onClick={() => setIsAddModalOpen(true)}
              isDisabled={isAddModalOpen}
              px={4}
              py={3}
            >
              <HStack spacing={3}>
                <Box
                  w="44px"
                  h="44px"
                  borderRadius="full"
                  border="1px dashed rgba(244,248,251,0.45)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <AddIcon color="rgba(244,248,251,0.78)" boxSize={3.5} />
                </Box>
                <Text color="#F4F8FB" fontSize="16px" fontWeight={400} m={0}>
                  Add more profile
                </Text>
              </HStack>
            </MenuItem>
          </Box>
        )}

        <Box borderTop="1px solid rgba(107,121,143,0.16)" py={1}>
          <MenuItem
            bg="transparent"
            border="none"
            _hover={{ bg: 'rgba(255,255,255,0.05)' }}
            px={4}
            py={3}
            onClick={() => navigate(routes.accountSettings.value)}
          >
            <HStack spacing={3}>
              <SettingsIcon color="rgba(244,248,251,0.58)" boxSize={5} />
              <Text color="rgba(244,248,251,0.78)" fontSize="16px" fontWeight={400} m={0}>
                Account Settings
              </Text>
            </HStack>
          </MenuItem>
          <MenuItem
            bg="transparent"
            border="none"
            _hover={{ bg: 'rgba(239,68,68,0.08)' }}
            px={4}
            py={3.5}
            onClick={() => authStore.logout()}
          >
            <HStack spacing={3}>
              <Text color="#ef4444" fontSize="20px" m={0} lineHeight={1}>
                ↪
              </Text>
              <Text color="#ef4444" fontSize="16px" fontWeight={400} m={0}>
                Log Out
              </Text>
            </HStack>
          </MenuItem>
        </Box>
      </MenuList>
      <EditAccountModal
        isProfile
        isOpen={isAccountModalOpen}
        toggle={toggleAccountModal}
        userId={userDetail?.id}
      />
      <AddProfileModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        addableTypes={addableTypes ?? []}
        onCreated={kind => {
          setIsAddModalOpen(false)
          onAddProfileSuccess?.(kind)
        }}
        onConflict={kind => {
          setIsAddModalOpen(false)
          ;(onAddProfileConflict ?? (() => profileStore.loadMyProfiles()))(kind)
        }}
      />
    </Menu>
  )
}

export default ProfileSwitcher
